import {
  Button,
  Col,
  Modal,
  Row,
  Table,
  TableProps,
  Tag,
  Checkbox,
  Space,
  Badge,
} from 'antd';
import {
  CheckerConfigsType,
  CheckerPropertiesType,
  DeviceTag,
  Device,
} from 'types/devices';
import { dateForDisplay } from 'libs/dateFormaterPetition';
import type { ColumnsType } from 'antd/es/table';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  UserAddOutlined,
} from '@ant-design/icons';
import RegisteredCheckerData from './RegisteredCheckerData';
import CommandsList from './DeviceCommands/CommandsList';
import { useState } from 'react';
import { ModalUserContent } from './DeviceCommands/ModalUserContent';
import { Link } from 'react-router';
import { useAppSelector } from 'hooks/reduxHooks';
import { getPassedMinutesFromDate } from 'hooks/getPassedMinutesFromDate';
import { maxDeviceIdleMinutes } from 'constants/global';
import 'styles/sass/devices.scss';
import { LastActivityData } from './LastActivityData';
import type { CheckboxProps } from 'antd';
import { EditDeviceModal } from './EditDeviceModal';

export const DevicesList = () => {
  const [openModal, setOpenModal] = useState(false);
  const [titleModal, setTitleModal] = useState<Device | undefined>();

  const [showInactiveDevices, setShowInactiveDevices] = useState(false);

  const { devices } = useAppSelector((state) => state.device);

  const showModal = (record: Device) => {
    if (record) {
      setTitleModal(record);
      setOpenModal(true);
    }
  };

  const handleCancel = () => {
    setOpenModal(false);
  };

  const tableProps: TableProps<Device> = {
    bordered: true,
    pagination: { position: ['bottomCenter'] },
    tableLayout: 'fixed',
    size: 'small',
    sticky: true,
    className: 'table-ps',
  };
  const columns: ColumnsType<Device> = [
    {
      title: 'Número de serie',
      key: 'noSerie',
      align: 'center',
      render: (record: Device) => (
        <div data-cy="device-serial-number">
          <div>
            {record.configs && (
              <Link to={`/dispositivos/${record.configs.SerialNumber}`}>
                {record.configs.SerialNumber ?? ''}
              </Link>
            )}
          </div>
          {record.tags &&
            record.tags.map((tag: DeviceTag) => (
              <Tag
                className="tag"
                key={tag._id}
                color={tag.tagColor}
                variant="solid"
              >
                {tag.tagName}
              </Tag>
            ))}
        </div>
      ),
    },
    {
      title: 'Estatus',
      align: 'center',
      responsive: ['lg', 'xl', 'xxl'],
      render: (record: Device) => (
        <div>
          {record.status == true ? (
            <Space>
              <Badge status="success" />
              Activo
            </Space>
          ) : (
            <Space>
              <Badge status="error" />
              Inactivo
            </Space>
          )}
        </div>
      ),
    },
    {
      title: 'Modelo',
      key: 'deviceName',
      align: 'center',
      dataIndex: 'configs',
      render: (configs: CheckerConfigsType) => <>{configs.DeviceName ?? ''}</>,
      sorter: (a, b) =>
        Number(a.configs.DeviceName) - Number(b.configs.DeviceName),
      sortDirections: ['descend', 'ascend'],
    },
    {
      title: 'IP de Dispositivo',
      key: 'ipDevice',
      align: 'center',
      dataIndex: 'properties',
      responsive: ['lg', 'xl', 'xxl'],
      render: (properties: CheckerPropertiesType) => (
        <>{properties.IPAddress ?? ''}</>
      ),
    },
    {
      title: 'Online',
      key: 'online',
      align: 'center',
      width: 90,
      responsive: ['lg', 'xl', 'xxl'],
      render: (record: Device) => (
        <>
          {getPassedMinutesFromDate(record.updatedAt) > maxDeviceIdleMinutes ? (
            <LastActivityData
              date={record.updatedAt}
              children={
                <CloseCircleOutlined className="status-icon offline-status-icon" />
              }
            />
          ) : (
            <CheckCircleOutlined className="status-icon online-status-icon" />
          )}
        </>
      ),
    },
    {
      title: 'Última actividad',
      key: 'date',
      dataIndex: 'updatedAt',
      render: (date) => dateForDisplay(date),
      sorter: (a, b) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      sortDirections: ['descend', 'ascend'],
      responsive: ['lg', 'xl', 'xxl'],
    },
    {
      title: 'Registros',
      key: 'date',
      width: 300,
      render: (data, record) => (
        <RegisteredCheckerData serialNumber={record.serialNumber} />
      ),
    },
    {
      title: 'Acciones',
      key: 'actions',
      align: 'center',
      width: 150,
      render: (a, record) => (
        <div className="option-buttons">
          <span title="Modificar Usuarios">
            <Button
              icon={<UserAddOutlined />}
              onClick={() => {
                showModal(record);
              }}
            />
          </span>
          <span title="Editar Dispositivo">
            <EditDeviceModal device={record} />
          </span>
        </div>
      ),
    },
  ];

  const handleShowInactiveDevices: CheckboxProps['onChange'] = (e) => {
    setShowInactiveDevices(e.target.checked);
  };
  const visibleDevices = devices.filter(
    (device) => showInactiveDevices || device.status
  );

  return (
    <>
      <h2 className="title mb-5">Lista de Dispositivos</h2>
      <Row>
        <Col xs={24}>
          <CommandsList devices={visibleDevices} />
          <Row justify="end" gutter={16} className="mb-4 mt-8">
            <Col>
              <Checkbox onChange={handleShowInactiveDevices}>
                Mostrar Inactivos
              </Checkbox>
            </Col>
          </Row>
          <Table
            {...tableProps}
            dataSource={visibleDevices}
            columns={columns}
            id="devicesTable"
            rowKey={'_id'}
          />
        </Col>
      </Row>
      <Modal
        title="Administrar usuarios"
        open={openModal}
        destroyOnHidden
        width="90vw"
        okButtonProps={{ disabled: true, style: { display: 'none' } }}
        onCancel={handleCancel}
      >
        <ModalUserContent deviceInfo={titleModal} closeModal={handleCancel} />
      </Modal>
    </>
  );
};
